import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout, StatusBadge } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Search, Filter, MoreVertical, CheckCircle, XCircle, Clock, PlayCircle, Download } from 'lucide-react';

// The arrangement decides what we pay on this client, so it belongs on the screen that lists
// them — it was not shown anywhere. Where a partner has ASKED for a different one, the answer
// is given here rather than on a separate queue: the value has to be displayed either way,
// and two buttons beside it is the whole workflow.
const ARRANGEMENT_LABEL = {
  referral: 'Referral',
  solution: 'Solution',
  affiliate: 'Affiliate',
  reseller: 'Reseller',
};

const ArrangementCell = ({ client, onDecide }) => {
  const current = client.arrangement || 'referral';
  const pending =
    client.arrangement_proposal_status === 'proposed' ? client.proposed_arrangement : null;

  return (
    <div className="flex flex-col gap-1">
      <span className="px-2 py-0.5 rounded bg-[#F4F4F1] text-[#1A1A1A] text-xs font-medium w-fit">
        {ARRANGEMENT_LABEL[current] || current}
      </span>
      {pending && (
        <div className="flex flex-col gap-1" data-testid={`arrangement-proposal-${client.id}`}>
          <span className="text-[11px] text-amber-700">
            asked for {ARRANGEMENT_LABEL[pending] || pending}
          </span>
          {client.arrangement_proposal_note && (
            <span className="text-[11px] text-[#64748B] italic max-w-[190px]">
              “{client.arrangement_proposal_note}”
            </span>
          )}
          <div className="flex gap-1">
            <Button
              size="sm"
              className="h-6 px-2 text-[11px] bg-[#ef785b] hover:bg-[#e56847] text-white"
              onClick={() => onDecide(client, true)}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[11px] text-[#64748B]"
              onClick={() => onDecide(client, false)}
            >
              Decline
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/** What this client was given by its partner, and what it has left.
 *
 *  Four distinct states, and collapsing any of them into "0" would lie:
 *    not linked   — no `org_id`. We have not matched this client record to a workspace, so
 *                   there is nothing to look up. It is a gap in our data, not an empty wallet.
 *    not funded   — a real org, but the partner does not bill it. Credits are not how this
 *                   client is paid for, so the column does not apply.
 *    given 0      — the partner funds it and has put nothing in. That one IS a zero, and it
 *                   is the one worth seeing: a reseller client with no credits is switched off.
 *    given N      — with what remains underneath, because given-and-spent and given-and-idle
 *                   are different clients.
 */
const ClientCreditsCell = ({ position, loaded, hasOrg }) => {
  const count = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);

  if (!hasOrg) {
    return <TableCell className="text-xs text-[#64748B]" title="No workspace linked to this client yet">—</TableCell>;
  }
  if (!loaded) return <TableCell className="text-sm text-[#64748B]">…</TableCell>;
  if (!position) return <TableCell className="text-sm text-[#64748B]">—</TableCell>;
  if (!position.funded_by_partner) {
    return <TableCell className="text-xs text-[#64748B]" title="This client is not billed through their partner">n/a</TableCell>;
  }
  return (
    <TableCell data-testid="client-credits-given">
      <p className="font-mono font-medium tabular-nums text-[#1A1A1A]">
        {count(position.credits_given)}
      </p>
      <p className="font-mono text-[11px] text-[#64748B]">{count(position.credits_balance)} left</p>
    </TableCell>
  );
};

const AdminClients = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');

  /* What each client was GIVEN by its partner. Lives on the platform, keyed by org id, so
     it arrives separately and merges per row — same reason as the partners list: folding it
     into /admin/clients would take this page down whenever the platform restarts. */
  const [creditPositions, setCreditPositions] = useState(null);

  useEffect(() => {
    fetchClients();
  }, [statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/client-credit-positions`, {
          headers: getAuthHeaders(),
        });
        setCreditPositions(res.data?.positions || {});
      } catch {
        setCreditPositions({});
      }
    })();
  }, [API_URL, getAuthHeaders]);

  const fetchClients = async () => {
    try {
      let url = `${API_URL}/api/admin/clients`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (clientId, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/clients/${clientId}/status?status=${status}`, null, {
        headers: getAuthHeaders()
      });
      toast.success(`Client status updated to ${status}`);
      fetchClients();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Answer a partner's request. Approval routes through the same backend function as setting
  // the arrangement directly, so there is one writer and one set of guards — and like every
  // arrangement change it applies forward only: commissions already raised keep their rate.
  const decideArrangement = async (client, approve) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/clients/${client.id}/arrangement-decision`,
        { approve },
        { headers: getAuthHeaders() }
      );
      toast.success(
        approve
          ? `${client.client_name} now runs as ${client.proposed_arrangement}. Applies to commissions from now on.`
          : `Request declined. ${client.client_name} stays on ${client.arrangement}.`
      );
      fetchClients();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.message || detail || 'Could not record that decision');
    }
  };

  const uniquePartners = [...new Set(clients.map(c => c.partner_name).filter(Boolean))].sort();

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.business_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = planFilter === 'all' || client.plan === planFilter;
    const matchesPartner = partnerFilter === 'all' || client.partner_name === partnerFilter;
    return matchesSearch && matchesPlan && matchesPartner;
  });

  const exportToCSV = () => {
    const headers = ['Client Name', 'Business', 'Partner', 'Plan', 'Monthly Value', 'Status', 'Referred On'];
    const rows = filteredClients.map(c => [
      c.client_name,
      c.business_name,
      c.partner_name || '',
      c.plan,
      c.monthly_value,
      c.status,
      formatDate(c.referred_on),
    ]);
    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-clients">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">
              Client Management
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              View and manage all clients across partners
            </p>
          </div>
          <Button variant="outline" className="border-[#E2E2DF]" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-white border-[#E2E2DF]">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  placeholder="Search by client or business name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 border-[#E2E2DF]"
                  data-testid="search-clients"
                />
              </div>
              <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                <SelectTrigger className="w-44 h-10 border-[#E2E2DF]">
                  <SelectValue placeholder="Partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Partners</SelectItem>
                  {uniquePartners.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger className="w-36 h-10 border-[#E2E2DF]">
                  <SelectValue placeholder="Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 h-10 border-[#E2E2DF]" data-testid="filter-status">
                  <Filter className="w-4 h-4 mr-2 text-[#64748B]" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white border-[#E2E2DF] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8] hover:bg-[#FAFAF8]">
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Client Name</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Business</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Partner</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Plan</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Monthly Value</TableHead>
                  {/* What the PARTNER put into this client, as opposed to what the client is
                      worth to us. On a reseller client these are unrelated numbers and the
                      partner-funded one is the only one that says whether it stays switched on. */}
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Credits Given</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Engagement</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Referred On</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef785b]"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-[#64748B]">
                      No clients found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client, index) => (
                    <TableRow key={client.id} className="hover:bg-[#FAFAF8]/50" data-testid={`client-row-${index}`}>
                      <TableCell className="font-medium text-[#1A1A1A]">{client.client_name}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{client.business_name}</TableCell>
                      <TableCell className="text-sm text-[#1A1A1A]">{client.partner_name}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded text-xs font-medium bg-[#FAFAF8] text-[#1A1A1A] capitalize">
                          {client.plan}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium text-green-600">{formatCurrency(client.monthly_value)}</TableCell>
                      <ClientCreditsCell
                        position={client.org_id ? creditPositions?.[client.org_id] : null}
                        loaded={creditPositions !== null}
                        hasOrg={!!client.org_id}
                      />
                      <TableCell>
                        <ArrangementCell client={client} onDecide={decideArrangement} />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={client.status} />
                      </TableCell>
                      <TableCell className="text-sm text-[#64748B]">{formatDate(client.referred_on)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`client-actions-${index}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {client.status === 'pending' && (
                              <DropdownMenuItem onClick={() => updateStatus(client.id, 'trial')}>
                                <PlayCircle className="w-4 h-4 mr-2 text-amber-600" /> Start Trial
                              </DropdownMenuItem>
                            )}
                            {client.status === 'trial' && (
                              <DropdownMenuItem onClick={() => updateStatus(client.id, 'active')}>
                                <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Convert to Active
                              </DropdownMenuItem>
                            )}
                            {client.status === 'active' && (
                              <DropdownMenuItem onClick={() => updateStatus(client.id, 'inactive')}>
                                <XCircle className="w-4 h-4 mr-2 text-red-600" /> Mark Inactive
                              </DropdownMenuItem>
                            )}
                            {client.status === 'inactive' && (
                              <DropdownMenuItem onClick={() => updateStatus(client.id, 'active')}>
                                <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Reactivate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminClients;
